/**
 * downloadUrl.ts - Save a remote file with a chosen filename.
 *
 * Exports come back as signed storage URLs, which are cross-origin. The `download`
 * attribute is ignored cross-origin, and the store sets no Content-Disposition, so a
 * plain <a download> would navigate instead of saving — an .md or .mp3 just opens in
 * a tab. Fetching to a blob first gives a same-origin object URL, which `download`
 * does honour.
 *
 * Requires CORS on the storage bucket; if that is not configured the fetch throws and
 * we fall back to opening the URL, which is still the file, just without a filename.
 *
 * Rejects when neither route worked, so the caller can tell the user. The fallback is
 * not a reliable safety net: it runs after an await, by which point the click's
 * transient activation may be gone and the popup blocked. Signed URLs are short-lived
 * (5 minutes on S3), so an expired link is a normal, reportable failure — not
 * something to swallow.
 */
export const downloadUrl = async (url: string, filename: string): Promise<void> => {
    try {
        const response = await fetch(url)
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`)
        }
        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        // Revoke once the browser has had a chance to start the save.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch (error) {
        if (!window.open(url, '_blank', 'noopener,noreferrer')) {
            throw error
        }
    }
}
